/**
 * Every URL the SPA answers to, declared once. `routes-map.tsx` builds its route
 * table from these constants and every component that navigates reads them, so
 * renaming a screen is an edit here and nowhere else.
 *
 * A path literal copied into a component is not a 404 waiting to happen, which
 * is why it survives review: renaming a route leaves the copy pointing at
 * nothing, the URL falls through to the catch-all, the catch-all sends it home,
 * and the guard sends it back to the login path it reads from this table — a
 * redirect LOOP, with no failing request to point at.
 *
 * This module imports nothing on purpose: the route table builds its elements at
 * module scope, so a component importing the table back would close a cycle that
 * blows up rather than resolving quietly. The definitions live here, where both
 * sides can reach them.
 */

/** `/login` — the sign-in screen, and where every expired session lands. */
export const LOGIN_PATH = '/login'

/** `/` — the plant's entry point: the operator's own groups. */
export const HOME_PATH = '/'

/** `/operaciones` — the plant operations area. */
export const OPERATIONS_PATH = '/operaciones'

/**
 * `/cuenta/contrasena` — changing one's OWN password. Signed in is the only
 * requirement: an operator needs it as much as a manager, so it lives outside the
 * management area. No tilde and no `ñ` in the segment on purpose — plant tablets
 * are shared and this URL gets read out loud and typed by hand.
 */
export const CHANGE_PASSWORD_PATH = '/cuenta/contrasena'

/** `/cuenta/pin` — one's OWN PIN for almacén's tablet. Signed in is the only requirement, as the password. */
export const CHANGE_PIN_PATH = '/cuenta/pin'

/**
 * `/admin` — the administration area: a sidebar with one entry per screen,
 * each shown to whoever holds its section (`admin`, `grupos`, `configuracion`).
 * The bare path answers nothing of its own: it redirects to the first entry the
 * person can see.
 */
export const ADMIN_PATH = '/admin'

/** `/admin/usuarios` — who works in the plant, and with what role (section `admin`). */
export const USERS_PATH = `${ADMIN_PATH}/usuarios`

/** `/admin/roles` — the roles and the sections each one opens (section `admin`). */
export const ROLES_PATH = `${ADMIN_PATH}/roles`

/** `/admin/grupos` — station groups and the types hung on each (section `grupos`). */
export const GROUPS_PATH = `${ADMIN_PATH}/grupos`

/**
 * `/admin/grupos/:id` — one group. The param is `id`, which is what
 * `useParams<{ id: string }>()` reads on the detail screen.
 */
export const GROUP_DETAIL_PATTERN = `${GROUPS_PATH}/:id`

/** `/admin/grupos/<uuid>` — the canonical URL of one group. Every link goes through
 * here rather than interpolating the path, so its shape is written once. */
export const groupDetailPath = (stationGroupId: string): string => `${GROUPS_PATH}/${stationGroupId}`

/** `/admin/asignaciones` — who is in which group right now (section `grupos`). */
export const ASSIGNMENTS_PATH = `${ADMIN_PATH}/asignaciones`

/**
 * `/admin/planificador/*` — the planner's configuration, one screen per table
 * that used to be a tab of the planning Sheet (section `configuracion`).
 */
const PLANNER_CONFIG_PATH = `${ADMIN_PATH}/planificador`

export const CONFIG_STATUS_PATH = `${PLANNER_CONFIG_PATH}/estado`
export const CONFIG_MACHINE_GROUPS_PATH = `${PLANNER_CONFIG_PATH}/maquinas`
export const CONFIG_PLAN_MACHINES_PATH = `${PLANNER_CONFIG_PATH}/maquinas-del-plan`
export const CONFIG_QUALITY_RULES_PATH = `${PLANNER_CONFIG_PATH}/calidad`
export const CONFIG_SPECIAL_SCHEDULE_PATH = `${PLANNER_CONFIG_PATH}/coccion-baja`
export const CONFIG_BM_HOMOLOGATIONS_PATH = `${PLANNER_CONFIG_PATH}/homologacion-bm`
export const CONFIG_SETTINGS_PATH = `${PLANNER_CONFIG_PATH}/parametros`
export const CONFIG_CHANGES_PATH = `${PLANNER_CONFIG_PATH}/historial`

/**
 * `/admin/mise-en-place/*` — the mise en place board's configuration: the
 * people of each day, the machines that are not shared, the margin and the
 * heat, and the rest of its parameters (section `configuracion`).
 */
const MISE_EN_PLACE_CONFIG_PATH = `${ADMIN_PATH}/mise-en-place`

export const MEP_STAFFING_PATH = `${MISE_EN_PLACE_CONFIG_PATH}/personas`
export const MEP_SHARED_MACHINES_PATH = `${MISE_EN_PLACE_CONFIG_PATH}/maquinas-que-no-se-comparten`
export const MEP_MARGIN_HEAT_PATH = `${MISE_EN_PLACE_CONFIG_PATH}/margen-y-calor`
export const MEP_SETTINGS_PATH = `${MISE_EN_PLACE_CONFIG_PATH}/parametros`

/**
 * `/admin/racionado/*` — the racionado boards' configuration: their alarms
 * and parameters, and the chillers' slots (section `configuracion`).
 */
const RACIONADO_CONFIG_PATH = `${ADMIN_PATH}/racionado`

export const RACIONADO_SETTINGS_PATH = `${RACIONADO_CONFIG_PATH}/alarmas`
export const CHILL_SLOTS_PATH = `${RACIONADO_CONFIG_PATH}/huecos`

/**
 * `/admin/pesaje/*` — the pesaje screen's configuration: the route through
 * the warehouse and its parameters (section `configuracion`).
 */
const PESAJE_CONFIG_PATH = `${ADMIN_PATH}/pesaje`

export const PESAJE_SETTINGS_PATH = `${PESAJE_CONFIG_PATH}/parametros`

/**
 * The URLs the management screens had before the administration area. They
 * only redirect now — bookmarks on plant tablets outlive a release — and no
 * component links to them.
 */
export const LEGACY_USERS_PATH = '/usuarios'
export const LEGACY_GROUPS_PATH = '/grupos'
export const LEGACY_GROUP_DETAIL_PATTERN = `${LEGACY_GROUPS_PATH}/:id`
export const LEGACY_ASSIGNMENTS_PATH = '/asignaciones'
